import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { InventoryItem } from '@court-sync/shared';
import {
  Button,
  Drawer,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Switch,
  Textarea,
} from '@/components/ui';
import { clubInventoryService } from '@/services/clubInventory';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { INVENTORY_ITEM_CREATE_TOUR_STEP_IDS } from '@/lib/tour-constants';

const ITEM_FORM_ID = 'inventory-item-form';

export interface InventoryItemFormValues {
  name: string;
  assetLabel: string;
  category: string;
  quantity: string;
  isHireable: boolean;
  tracksStock: boolean;
  lowStockThreshold: string;
  hirePrice: string;
  notes: string;
  isActive: boolean;
}

interface InventoryItemDrawerProps {
  clubId: string;
  currency: string;
  item: InventoryItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  onCreatePanelOpen?: () => void;
  showCreateTourHelp?: boolean;
  showBackdrop?: boolean;
}

function toFormValues(item: InventoryItem | null): InventoryItemFormValues {
  if (!item) {
    return {
      name: '',
      assetLabel: '',
      category: '',
      quantity: '1',
      isHireable: false,
      tracksStock: true,
      lowStockThreshold: '',
      hirePrice: '',
      notes: '',
      isActive: true,
    };
  }

  return {
    name: item.name,
    assetLabel: item.assetLabel ?? '',
    category: item.category ?? '',
    quantity: String(item.quantity),
    isHireable: item.isHireable,
    tracksStock: item.tracksStock,
    lowStockThreshold: item.lowStockThreshold != null ? String(item.lowStockThreshold) : '',
    hirePrice: item.hirePriceCents != null ? (item.hirePriceCents / 100).toFixed(2) : '',
    notes: item.notes ?? '',
    isActive: item.isActive,
  };
}

export function InventoryItemDrawer({
  clubId,
  currency,
  item,
  isOpen,
  onClose,
  onSaved,
  onCreatePanelOpen,
  showCreateTourHelp = false,
  showBackdrop = true,
}: InventoryItemDrawerProps) {
  const isEditing = item != null;

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<InventoryItemFormValues>({
    defaultValues: toFormValues(item),
  });

  const isHireable = watch('isHireable');
  const tracksStock = watch('tracksStock');

  useEffect(() => {
    if (isOpen) {
      reset(toFormValues(item));
    }
  }, [isOpen, item, reset]);

  const onSubmit = async (values: InventoryItemFormValues) => {
    const quantity = Number.parseInt(values.quantity, 10);
    const lowStockThreshold = values.lowStockThreshold.trim()
      ? Number.parseInt(values.lowStockThreshold, 10)
      : null;
    const hirePrice = values.hirePrice.trim() ? Number.parseFloat(values.hirePrice) : null;
    const hirePriceCents = hirePrice != null ? Math.round(hirePrice * 100) : null;

    if (!values.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!Number.isFinite(quantity) || quantity < 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    if (!values.isHireable && !values.tracksStock) {
      toast.error('Item must be hireable, track stock, or both');
      return;
    }
    if (values.isHireable && (hirePriceCents == null || hirePriceCents < 0)) {
      toast.error('Hire price is required for hireable items');
      return;
    }

    const payload = {
      name: values.name.trim(),
      assetLabel: values.assetLabel.trim() || null,
      category: values.category.trim() || null,
      quantity,
      isHireable: values.isHireable,
      tracksStock: values.tracksStock,
      lowStockThreshold,
      hirePriceCents: values.isHireable ? hirePriceCents : null,
      currency,
      notes: values.notes.trim() || null,
      isActive: values.isActive,
    };

    const res = isEditing
      ? await clubInventoryService.updateItem(clubId, item.id, payload)
      : await clubInventoryService.createItem(clubId, payload);

    if (res.success) {
      toast.success(isEditing ? 'Item updated' : 'Item created');
      onSaved();
      onClose();
    } else {
      toast.error(res.error || (isEditing ? 'Failed to update item' : 'Failed to create item'));
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      showBackdrop={showBackdrop}
      title={isEditing ? 'Edit inventory item' : 'Add inventory item'}
      description="Track stock levels and/or make items available as court hire add-ons."
      width="lg"
      panelId={!isEditing ? INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.DRAWER : undefined}
      onPanelOpen={!isEditing ? onCreatePanelOpen : undefined}
      headerActions={
        showCreateTourHelp ? (
          <DrawerTourHelp
            tourKey="club-inventory-item-create"
            suggestion="New to inventory? Tour this form before you save."
            buttonLabel="Tour this form"
          />
        ) : undefined
      }
      footer={
        <div
          id={!isEditing ? INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.SUBMIT : undefined}
          className="flex justify-end gap-2"
        >
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={ITEM_FORM_ID} isLoading={isSubmitting}>
            {isEditing ? 'Save item' : 'Create item'}
          </Button>
        </div>
      }
    >
      <form id={ITEM_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div id={!isEditing ? INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.DETAILS : undefined}>
        <Field data-invalid={!!errors.name?.message}>
          <FieldLabel htmlFor="inventory-name">Name</FieldLabel>
          <Input
            id="inventory-name"
            placeholder="e.g. Ball machine"
            {...register('name', { required: 'Name is required' })}
          />
          <FieldError>{errors.name?.message}</FieldError>
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="inventory-category">Category</FieldLabel>
            <Input id="inventory-category" placeholder="e.g. Equipment" {...register('category')} />
          </Field>
          <Field>
            <FieldLabel htmlFor="inventory-asset-label">SKU / asset tag</FieldLabel>
            <Input id="inventory-asset-label" placeholder="Optional" {...register('assetLabel')} />
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="inventory-quantity">Quantity owned</FieldLabel>
          <Input
            id="inventory-quantity"
            type="number"
            min={0}
            {...register('quantity', { required: 'Quantity is required' })}
          />
        </Field>
        </div>

        <div
          id={!isEditing ? INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.OPTIONS : undefined}
          className="space-y-4 rounded-lg border border-border p-4"
        >
          <div className="flex items-start gap-3">
            <Controller
              name="tracksStock"
              control={control}
              render={({ field }) => (
                <Switch id="inventory-tracks-stock" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
            <label htmlFor="inventory-tracks-stock" className="cursor-pointer text-sm">
              <span className="font-medium text-gray-900 dark:text-white">Track stock</span>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                Show quantity on hand and low-stock alerts.
              </p>
            </label>
          </div>

          {tracksStock ? (
            <Field>
              <FieldLabel htmlFor="inventory-low-stock">Low stock threshold</FieldLabel>
              <Input
                id="inventory-low-stock"
                type="number"
                min={0}
                placeholder="Optional"
                {...register('lowStockThreshold')}
              />
            </Field>
          ) : null}

          <div className="flex items-start gap-3">
            <Controller
              name="isHireable"
              control={control}
              render={({ field }) => (
                <Switch id="inventory-hireable" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
            <label htmlFor="inventory-hireable" className="cursor-pointer text-sm">
              <span className="font-medium text-gray-900 dark:text-white">Available for hire</span>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                Can be added during court hire checkout or logged as a walk-up loan.
              </p>
            </label>
          </div>

          {isHireable ? (
            <Field>
              <FieldLabel htmlFor="inventory-hire-price">Hire price per hour ({currency})</FieldLabel>
              <Input
                id="inventory-hire-price"
                type="number"
                min={0}
                step={0.01}
                placeholder="0.00"
                {...register('hirePrice')}
              />
            </Field>
          ) : null}
        </div>

        <Field id={!isEditing ? INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.NOTES : undefined}>
          <FieldLabel htmlFor="inventory-notes">Notes</FieldLabel>
          <Textarea id="inventory-notes" rows={3} {...register('notes')} />
        </Field>

        <div
          id={!isEditing ? INVENTORY_ITEM_CREATE_TOUR_STEP_IDS.ACTIVE : undefined}
          className="flex items-start gap-3"
        >
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Switch id="inventory-active" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
          <label htmlFor="inventory-active" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Active</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Inactive items are hidden from hire and stock lists.
            </p>
          </label>
        </div>
      </form>
    </Drawer>
  );
}
